import React, { useCallback, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useSelected, useFocused } from 'slate-react';
import { Columns, Settings, X } from 'lucide-react';

// Preset column layouts
const COLUMN_PRESETS = {
  '2-equal': { label: '2 Equal', widths: ['50%', '50%'] },
  '2-left': { label: '2/3 + 1/3', widths: ['66%', '34%'] },
  '2-right': { label: '1/3 + 2/3', widths: ['34%', '66%'] },
  '3-equal': { label: '3 Equal', widths: ['33.33%', '33.33%', '33.34%'] },
  '4-equal': { label: '4 Equal', widths: ['25%', '25%', '25%', '25%'] },
};

// Settings Panel Portal - renders outside the editor to avoid Slate DOM resolution errors
function SettingsPortal({ children, buttonRef }) {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.right + window.scrollX - 300, // Panel width
      });
    }
  }, [buttonRef]);

  return ReactDOM.createPortal(
    <div 
      style={{
        position: 'absolute',
        top: position.top,
        left: Math.max(16, position.left),
        zIndex: 9999,
      }}
    >
      {children}
    </div>,
    document.body
  );
}

function ColumnGroupElement({ 
  attributes, 
  children, 
  element, 
  onUpdate, 
  onDelete,
}) {
  const selected = useSelected();
  const focused = useFocused();
  const [showSettings, setShowSettings] = useState(false);
  const settingsButtonRef = useRef(null);

  const {
    preset = '2-equal',
    gap = 16,
  } = element;

  // Close settings when clicking outside
  useEffect(() => {
    if (showSettings) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.column-settings-panel') && 
            !e.target.closest('.column-settings-button')) {
          setShowSettings(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSettings]);

  // Get column count from children
  const columnCount = React.Children.count(children);

  const handlePresetChange = useCallback((newPreset) => {
    if (onUpdate) {
      onUpdate(element.id, { preset: newPreset });
    }
  }, [element.id, onUpdate]);

  const handleGapChange = useCallback((newGap) => {
    if (onUpdate) {
      onUpdate(element.id, { gap: parseInt(newGap) });
    }
  }, [element.id, onUpdate]);

  // Proper event handlers for Slate
  const handleSettingsClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSettings(!showSettings);
  }, [showSettings]);

  const handleDeleteClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) {
      onDelete(element.id);
    }
  }, [element.id, onDelete]);

  const handleCloseSettings = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSettings(false);
  }, []);

  return (
    <div 
      {...attributes}
      className={`column-group-element relative my-2 p-1 rounded-lg transition-colors ${
        selected && focused ? 'ring-2 ring-blue-500 bg-blue-50/30' : 'hover:ring-1 hover:ring-gray-300'
      }`}
    >
      {/* Controls - visible when selected or on hover */}
      <div 
        className={`absolute -top-3 right-2 flex items-center gap-1 z-10 transition-opacity ${
          selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
        contentEditable={false}
        style={{ opacity: selected ? 1 : undefined }}
      >
        <button
          ref={settingsButtonRef}
          type="button"
          onMouseDown={handleSettingsClick}
          className="column-settings-button p-1.5 bg-white rounded shadow-md hover:bg-gray-100 cursor-pointer"
          title="Column Settings"
        >
          <Settings size={14} />
        </button>
        {onDelete && (
          <button
            type="button"
            onMouseDown={handleDeleteClick}
            className="p-1.5 bg-white rounded shadow-md hover:bg-red-100 cursor-pointer"
            title="Delete Columns"
          >
            <X size={14} className="text-red-500" />
          </button>
        )}
      </div>

      {/* Settings Panel - rendered via portal */}
      {showSettings && (
        <SettingsPortal buttonRef={settingsButtonRef}>
          <div 
            className="column-settings-panel bg-white rounded-lg shadow-2xl border p-4 min-w-[300px]"
            contentEditable={false}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-sm">Column Settings</h4>
              <button
                type="button"
                onMouseDown={handleCloseSettings}
                className="p-1 hover:bg-gray-100 rounded cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>
            
            {/* Preset */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-2">Layout Preset</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(COLUMN_PRESETS).map(([key, { label, widths }]) => (
                  <button
                    key={key}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePresetChange(key);
                    }}
                    className={`p-2 text-xs border rounded-md cursor-pointer transition-colors ${
                      preset === key 
                        ? 'bg-blue-100 border-blue-500 text-blue-700' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex gap-1 mb-1 h-4">
                      {widths.map((w, i) => (
                        <div 
                          key={i}
                          className={`h-full rounded-sm ${preset === key ? 'bg-blue-400' : 'bg-gray-300'}`}
                          style={{ width: w }}
                        />
                      ))}
                    </div>
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Gap */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Gap between columns: {gap}px
              </label>
              <input
                type="range"
                min="0"
                max="64"
                value={gap}
                onChange={(e) => handleGapChange(e.target.value)}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0</span>
                <span>64px</span>
              </div>
            </div>

            <div className="text-xs text-gray-500 mt-3 p-2 bg-gray-50 rounded">
              <strong>Tip:</strong> Click inside a column to add text. Use the + button or the toolbar to add blocks.
            </div>
          </div>
        </SettingsPortal>
      )}

      {/* Columns Container */}
      <div 
        className="columns-grid flex"
        style={{ gap: `${gap}px` }}
      >
        {children}
      </div>

      {/* Empty State */}
      {columnCount === 0 && (
        <div 
          className="empty-columns flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-300 rounded-lg"
          contentEditable={false}
        >
          <Columns size={48} className="text-gray-400 mb-4" />
          <p className="text-gray-500 mb-4">Select a column layout</p>
          <div className="flex gap-2 flex-wrap justify-center">
            {Object.entries(COLUMN_PRESETS).map(([key, { label }]) => (
              <button
                key={key}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePresetChange(key);
                }}
                className="px-3 py-2 text-sm border rounded-lg hover:bg-blue-50 cursor-pointer"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Column Item Element - Individual column that can contain content
function ColumnElement({ attributes, children, element }) {
  const selected = useSelected();
  const focused = useFocused();

  const width = element.width || '50%';

  return (
    <div 
      {...attributes}
      className={`column-element relative min-h-[60px] p-2 rounded-lg transition-all ${
        selected && focused 
          ? 'border-2 border-blue-400 bg-blue-50/30 shadow-sm' 
          : 'border border-dashed border-gray-200 hover:border-gray-400 hover:bg-gray-50/50'
      }`}
      style={{ 
        width,
        minWidth: '60px',
        flexShrink: 1,
        flexGrow: 0,
        boxSizing: 'border-box',
      }}
    >
      {/* Slate children must be direct children of the attributed element */}
      {children}
    </div>
  );
}

export { ColumnGroupElement, ColumnElement };
export default ColumnGroupElement;
